import { useTranslation } from "react-i18next";
import { TriangleAlert } from "lucide-react";
import { BONUS_BLOCKERS, type BonusType, type CommissionParams } from "@nh/shared";
import { Checkbox, Input } from "@/shared/ui/core";

/**
 * ═══════════════════════════════════════════════════════════════════
 * LOS DEMÁS BONOS DEL PLAN (Anahí, 29-ago: "¿por qué solo tenemos esas
 * comisiones? ¿dónde están las demás, como en la versión anterior?").
 *
 * Estaban: `commissionParamsSchema` guarda BAR, los tres fondos,
 * lealtad y la regla 40/60 desde el primer día, y el servidor los valida
 * y los persiste. Lo que faltaba era la pantalla — el panel solo pintaba
 * los bonos "por niveles" (BIR, BUR), que son los únicos con
 * gradiente, y los demás quedaban invisibles aunque el plan los llevara
 * dentro.
 *
 * ── Cada uno dice QUÉ LE FALTA para pagar ────────────────────────────
 * `BONUS_BLOCKERS` ya lo tenía escrito y nadie lo enseñaba. Es la
 * diferencia entre "está apagado" y "está encendido pero el motor
 * todavía no lo calcula": sin eso, encender la casilla haría creer que
 * el bono empieza a pagar, y así es como producción acabó con siete
 * bonos habilitados que no pagaban nada.
 *
 * Se editan los números que son del NEGOCIO —porcentajes, mínimos,
 * meses—; los catálogos que dependen del rango (los hitos del BAR, los
 * tramos de lealtad) se enseñan como resumen: tocarlos pide una tabla
 * propia, no un renglón suelto.
 * ═══════════════════════════════════════════════════════════════════
 */
/*
 * Lealtad se quitó de la pantalla (Anahí, 29-ago: "creo que no es
 * necesaria por ahora"). El bono SIGUE en el plan y en la base con sus
 * datos —5% del precio, 3 tramos— y se guarda intacto en cada
 * actualización: lo que desapareció es su pestaña, no su configuración.
 * Volver a mostrarla es añadir su entrada y su caso, nada más.
 */
export interface OtrosDraft {
  barEnabled: boolean;
  barFundPct: string;
  barPriorMonths: string;
  fundsEnabled: boolean;
  rule4060Enabled: boolean;
  rule4060MinVp: string;
  rule4060MinClients: string;
}

export function otrosDraftFrom(params: CommissionParams): OtrosDraft {
  return {
    barEnabled: params.bar.enabled,
    barFundPct: String(params.bar.fundBpsOfCommissionableVolume / 100),
    barPriorMonths: String(params.bar.requiredQualifiedPriorMonths),
    fundsEnabled: params.globalFunds.enabled,
    rule4060Enabled: params.rule4060.enabled,
    rule4060MinVp: String(params.rule4060.minPersonalVolumePoints),
    rule4060MinClients: String(params.rule4060.minExternalClients),
  };
}

/** El aviso de por qué un bono no paga todavía. `null` = sí puede. */
function Bloqueo({ tipo }: { tipo: BonusType }) {
  const { t } = useTranslation();
  const motivo = BONUS_BLOCKERS[tipo];
  if (!motivo) return null;
  return (
    <p className="flex items-start gap-2 rounded-2xl bg-amber-500/10 px-4 py-3 text-[12.5px] leading-relaxed text-amber-700">
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
      <span>
        <strong>{t("Todavía no paga.")}</strong> {t(motivo)}
      </span>
    </p>
  );
}

function Campo({
  label,
  value,
  onChange,
  suffix,
  ...rest
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  suffix?: string;
  min?: number;
  max?: number;
}) {
  return (
    <label className="grid gap-1 text-[12.5px] font-medium text-ds-ink-3">
      {label}
      <div className="flex w-40 items-center gap-2">
        <Input
          type="number"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          {...rest}
        />
        {suffix && <span className="text-[12px]">{suffix}</span>}
      </div>
    </label>
  );
}

export function OtrosBonos({
  tab,
  params,
  draft,
  onChange,
}: {
  tab: "bar" | "fondos" | "rule4060";
  params: CommissionParams;
  draft: OtrosDraft;
  onChange: (update: Partial<OtrosDraft>) => void;
}) {
  const { t } = useTranslation();

  if (tab === "bar") {
    return (
      <div className="grid gap-4 p-5">
        <Bloqueo tipo="BAR" />
        <Checkbox
          label={t("BAR habilitado")}
          checked={draft.barEnabled}
          onChange={(event) => onChange({ barEnabled: event.target.checked })}
        />
        <div className="flex flex-wrap gap-5">
          <Campo
            label={t("Fondo (% del volumen comisionable)")}
            value={draft.barFundPct}
            onChange={(barFundPct) => onChange({ barFundPct })}
            suffix="%"
            min={0}
            max={100}
          />
          <Campo
            label={t("Meses calificados previos")}
            value={draft.barPriorMonths}
            onChange={(barPriorMonths) => onChange({ barPriorMonths })}
            min={0}
            max={12}
          />
        </div>
        <p className="text-[12.5px] text-ds-ink-3">
          {t("{{n}} hitos de rango configurados. Se editan con el catálogo de rangos.", {
            n: params.bar.milestones.length,
          })}
        </p>
      </div>
    );
  }

  if (tab === "fondos") {
    return (
      <div className="grid gap-4 p-5">
        <Bloqueo tipo="FUND_GLOBAL" />
        <Checkbox
          label={t("Fondos habilitados")}
          checked={draft.fundsEnabled}
          onChange={(event) => onChange({ fundsEnabled: event.target.checked })}
        />
        <dl className="grid gap-2 text-[13px] sm:grid-cols-3">
          {(
            [
              ["Fondo global", params.globalFunds.fundGlobal],
              ["Fondo legado", params.globalFunds.fundLegacy],
              ["Fondo leyenda", params.globalFunds.fundLegend],
            ] as const
          ).map(([label, fund]) => (
            <div key={label} className="rounded-2xl bg-ds-mist/40 px-4 py-3">
              <dt className="text-[12px] font-semibold text-ds-ink-3">{t(label)}</dt>
              <dd className="mt-0.5 font-mono text-[15px] font-semibold text-ds-ink">
                {fund.bpsOfCommissionableVolume / 100}%
              </dd>
              <dd className="text-[12px] text-ds-ink-3">
                {t("desde rango {{min}}", { min: fund.minRank })}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    );
  }

  return (
    <div className="grid gap-4 p-5">
      <p className="rounded-2xl bg-ds-lavender-bg/60 px-4 py-3 text-[12.5px] leading-relaxed text-ds-ink-2">
        {t(
          "No es un bono: es la condición que hay que cumplir para cobrar los que la piden. Hoy bloquea:",
        )}{" "}
        <strong>{params.rule4060.blocks.join(", ") || t("ninguno")}</strong>.
      </p>
      <Checkbox
        label={t("Regla 40/60 habilitada")}
        checked={draft.rule4060Enabled}
        onChange={(event) => onChange({ rule4060Enabled: event.target.checked })}
      />
      <div className="flex flex-wrap gap-5">
        <Campo
          label={t("VP personal mínimo")}
          value={draft.rule4060MinVp}
          onChange={(rule4060MinVp) => onChange({ rule4060MinVp })}
          suffix="VP"
          min={0}
        />
        <Campo
          label={t("Clientes externos mínimos")}
          value={draft.rule4060MinClients}
          onChange={(rule4060MinClients) => onChange({ rule4060MinClients })}
          min={0}
          max={100}
        />
      </div>
      <p className="text-[12.5px] text-ds-ink-3">
        {t("Volumen de clientes exigido: {{y1}}% el primer año, {{y2}}% después.", {
          y1: params.rule4060.clientVolumeBps.year1 / 100,
          y2: params.rule4060.clientVolumeBps.year2Plus / 100,
        })}
      </p>
    </div>
  );
}
